import PropTypes from 'prop-types';
import { memo } from 'react';
import { twJoin } from 'tailwind-merge';

import Icon from 'shopper/components/Icon';

const PHASE_CONFIG = {
  active: {
    label: 'Termina em',
    icon: 'bolt',
    ariaPrefix: 'Oferta encerra em',
  },
  'pre-activation': {
    label: 'Começa em',
    icon: 'clock-filled',
    ariaPrefix: 'Oferta começa em',
  },
};

const padZero = (value) => String(value).padStart(2, '0');

const buildAriaLabel = (ariaPrefix, hours, minutes, seconds) => {
  const parts = [];

  if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);
  if (minutes > 0)
    parts.push(`${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`);
  if (seconds > 0 || parts.length === 0)
    parts.push(`${seconds} ${seconds === 1 ? 'segundo' : 'segundos'}`);

  return `${ariaPrefix} ${parts.join(', ')}`;
};

const SIZE_CLASSES = {
  xs: {
    container: 'h-6 gap-1 px-2',
    icon: 'text-[#E0157A] dark:text-[#E0157A] size-3',
    iconSize: 'size3',
    label: 'font-rubik font-medium text-[10px] leading-none text-[#011327]',
    time: 'font-bold tracking-[1px] text-[10px] leading-none text-[#011327]',
  },
  sm: {
    container: 'h-7 gap-2 px-6 max-md:h-6 max-md:gap-1 max-md:px-2',
    icon: 'text-[#E0157A] dark:text-[#E0157A] max-md:size-3.5',
    iconSize: 'size3',
    label:
      'font-rubik font-medium text-[#011327] text-xs leading-[8px] whitespace-nowrap',
    time: 'font-bold tracking-[1.2px] text-[#011327] text-xs leading-[8px] whitespace-nowrap',
  },
  lg: {
    container: 'h-8 gap-2 px-6 max-md:h-7 max-md:px-4',
    icon: 'text-[#E0157A] dark:text-[#E0157A]',
    iconSize: 'size3',
    label:
      'font-rubik font-medium text-[16px] text-[#011327] max-md:text-[14px]',
    time: 'font-bold tracking-[1.2px] text-[16px] text-[#011327]',
  },
};

const StopWatch = ({ phase, hours, minutes, seconds, size = 'sm' }) => {
  const config = PHASE_CONFIG[phase] || PHASE_CONFIG.active;
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.sm;

  const ariaLabel = buildAriaLabel(config.ariaPrefix, hours, minutes, seconds);

  return (
    <div
      aria-label={ariaLabel}
      className={twJoin(
        'flex items-center justify-center rounded-3 bg-[#fce8f2]',
        sizeClasses.container
      )}
      role="timer"
    >
      <Icon
        className={sizeClasses.icon}
        name={config.icon}
        size={sizeClasses.iconSize}
        aria-hidden
      />
      <span className={sizeClasses.label}>{config.label}</span>
      <span
        className={sizeClasses.time}
        style={{ fontFamily: 'Menlo, monospace' }}
      >
        {padZero(hours)}:{padZero(minutes)}:{padZero(seconds)}
      </span>
    </div>
  );
};

StopWatch.propTypes = {
  hours: PropTypes.number.isRequired,
  minutes: PropTypes.number.isRequired,
  phase: PropTypes.oneOf(['active', 'pre-activation']).isRequired,
  seconds: PropTypes.number.isRequired,
  size: PropTypes.oneOf(['xs', 'sm', 'lg']),
};

export default memo(StopWatch);
